import React, { useEffect, ReactNode } from 'react';
import { useHistory } from 'react-router';
import { useOnboardingContext } from '../../../contexts/Onboarding';
import { useGetOnboardingPage } from '../../../hooks/useOnboardingPage';
import { useGetOnboardingProgress } from './utils/use-get-onboarding-progress';
import { forwardUrlParams } from '../../../utils';
import { makeStyles } from '@material-ui/core/styles';
import { Container } from '@material-ui/core';
import ContentContainer from '../../BuildingBlocks/Container/ContentContainer';
import ProgressLine from '../../BuildingBlocks/Progress/ProgressLine';
import Header from './Header/Header';
import CheckServer from './CheckServer';
import LoadingBackdrop from '../../LoadingBackdrop';

const useStyles = makeStyles((theme: any) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: 3000,
    paddingTop: theme.spacing(6),
    paddingLeft: theme.spacing(6),
    paddingRight: theme.spacing(6),
    position: 'relative',
    [theme.breakpoints.down('sm')]: {
      paddingLeft: theme.spacing(0.5),
      paddingRight: theme.spacing(0.5),
      paddingTop: 0,
    },
  },
  hideProgressBar: { height: 72 },
}));

interface Props {
  title?: string | ReactNode;
  subtitle?: string | ReactNode;
  headerWidth?: number;
  contentWidth?: string | number;
  children: ReactNode;
  hideContent?: boolean;
  fetchLines?: boolean;
  showLoading?: boolean;
}

export default function OnboardingStepper({
  title,
  subtitle,
  headerWidth = 875,
  contentWidth = '100%',
  children,
  hideContent = false,
  fetchLines = false,
  showLoading = true,
}: Props) {
  const classes = useStyles();
  const history = useHistory();
  const page = useGetOnboardingPage();
  const onboardingProgress = useGetOnboardingProgress();
  const {
    loading,
    pageChanged,
    activeStep,
    setActiveStep,
    finishedSettingPageNumbers,
  } = useOnboardingContext();

  useEffect(() => {
    if (!finishedSettingPageNumbers) return;
    if (page && activeStep) {
      history.push({ pathname: page.url, search: forwardUrlParams() });
    }
    if (page && !activeStep) {
      setActiveStep(page.stepInStepper);
    }
  }, [
    page,
    pageChanged,
    activeStep,
    setActiveStep,
    finishedSettingPageNumbers,
    history,
  ]);

  return (
    <>
      <LoadingBackdrop open={showLoading && loading} />
      <CheckServer fetchLines={fetchLines} />
      <Container className={classes.container}>
        {!hideContent && (
          <>
            {page?.hideProgressBar ? (
              <div className={classes.hideProgressBar} />
            ) : (
              <ProgressLine progressPercent={onboardingProgress} />
            )}
            <Header title={title} subtitle={subtitle} width={headerWidth} />
            <ContentContainer width={contentWidth}>{children}</ContentContainer>
          </>
        )}
      </Container>
    </>
  );
}
